/**
 * The little the tour keeps in the browser — conveniences, never progress.
 *
 * Progress is the server's (see `resolve-step.ts`). What lives here is only what the server has
 * no reason to know: whether this browser has already been greeted, whether the tour is running
 * in this tab right now, and which customer it was walking through. Every key carries the shop,
 * so one phone shared by two shops keeps them apart.
 *
 * Storage can be missing or throw (a private window, blocked site data), so every access is
 * wrapped; losing it only means the welcome may be shown once more.
 */

export type TourRunState = 'running' | 'paused';

function read(store: () => Storage, key: string): string | null {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
}

function write(store: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) store().removeItem(key);
    else store().setItem(key, value);
  } catch {
    // Nothing to do: the tour works without it.
  }
}

const local = (): Storage => window.localStorage;
const session = (): Storage => window.sessionStorage;

export function tourStorage(shopId: string) {
  const key = (name: string) => `crm.tour.${name}.${shopId}`;
  return {
    welcomed: () => read(local, key('welcomed')) === '1',
    markWelcomed: () => write(local, key('welcomed'), '1'),

    celebrated: () => read(local, key('celebrated')) === '1',
    markCelebrated: () => write(local, key('celebrated'), '1'),

    customerId: () => read(local, key('customer')),
    setCustomerId: (id: string) => write(local, key('customer'), id),

    runState: (): TourRunState | null => {
      const value = read(session, key('run'));
      return value === 'running' || value === 'paused' ? value : null;
    },
    setRunState: (value: TourRunState | null) => write(session, key('run'), value),
  };
}

export type TourStorage = ReturnType<typeof tourStorage>;
